import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { breadcrumbSchema } from "../data/seo";

const restorativeTreatments = [
  {
    title: "Tooth-Colored Fillings",
    body: "Tooth-colored fillings repair cavities and smaller areas of damage. When clinically appropriate, the material can be selected to blend naturally with the surrounding enamel.",
  },
  {
    title: "Dental Crowns",
    body: "A custom crown restores strength, shape, and protection to a tooth that has been weakened, fractured, or extensively restored. Its form, shade, and relationship to the bite are carefully considered.",
  },
  {
    title: "Inlays and Onlays",
    body: "When a tooth needs more support than a filling but may not require a full crown, an inlay or onlay can provide a conservative solution while preserving healthy tooth structure whenever possible.",
  },
  {
    title: "Root Canal Treatment",
    body: "When the tissue inside a tooth becomes inflamed or infected, root canal treatment may make it possible to preserve the natural tooth. After treatment, the tooth is properly restored to protect its strength and function.",
  },
  {
    title: "Dental Bridges",
    body: "A dental bridge replaces one or more missing teeth with a fixed restoration. It is designed to complete the smile while restoring comfortable function.",
  },
  {
    title: "Dental Implants",
    body: "Dental implants can provide a stable foundation for replacement teeth. Depending on individual needs, they may support a crown, bridge, or certain types of dentures. Suitability depends on oral health, general health, bone support, and other clinical considerations.",
  },
  {
    title: "Full and Partial Dentures",
    body: "Full and partial dentures replace multiple missing teeth and restore support, function, and appearance. Options may include conventional and implant-supported designs.",
  },
];

export const RestorativeDentistryPage = () => {
  return (
    <div data-testid="restorative-dentistry-page">
      <Seo
        title="Restorative Dentistry | Smiles by Dr. K"
        description="Thoughtful restorative dentistry focused on comfort, function, and natural-looking outcomes tailored to your smile."
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: "Restorative Dentistry", path: "/services/restorative-dentistry" },
          ]),
        ]}
      />

      <section className="relative h-[65vh] min-h-[500px] overflow-hidden">
        <img src={IMAGES.clinicInterior1} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/65" />
        <div className="relative h-full container-wide flex flex-col justify-end pb-16 text-white">
          <div className="label-eyebrow text-gold mb-6">Restorative Dentistry</div>
          <h1 className="heading-display text-5xl md:text-7xl lg:text-8xl text-white max-w-5xl">
            Thoughtfully Restoring the Smile That Belongs to You
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-24 container-x">
        <div className="max-w-4xl text-ink/80 leading-[1.85] space-y-6">
          <p>
            A beautiful smile is not defined by perfection. It is defined by health, comfort, and the confidence to enjoy life without thinking about your teeth.
          </p>
          <p>
            Restorative dentistry repairs teeth affected by decay, damage, infection, or wear and replaces teeth that have been lost. Each treatment is shaped around the individual, with careful attention to function, appearance, and long-term oral health.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-cream/50">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Restoration" title="Restore What Time," italicWord="Wear, or Life Has Changed" />
          </div>
          <div className="lg:col-span-7 text-ink/80 leading-[1.8] space-y-6">
            <p>
              Teeth are remarkably strong, but they are not indestructible. Everyday wear, decay, injury, and aging dental work can gradually affect their strength and appearance. Missing teeth can also change the way you bite, eat, speak, and smile.
            </p>
            <p>
              Restorative dentistry brings these elements back into balance. The goal is not simply to repair an individual tooth. It is to create a smile that feels comfortable, functions naturally, and still looks unmistakably like your own.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <SectionHeading eyebrow="Treatments" title="Restorative" italicWord="Treatments" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10">
          {restorativeTreatments.map((item, index) => (
            <article key={item.title} className="bg-white p-8 md:p-10 reveal" style={{ transitionDelay: `${index * 50}ms` }}>
              <h3 className="font-serif text-3xl font-light leading-tight text-ink">{item.title}</h3>
              <p className="mt-4 text-sm leading-[1.85] text-ink/70">{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-ink text-bone">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Personalization" title="Care Designed Around" italicWord="the Individual" light />
          </div>
          <div className="lg:col-span-7 leading-[1.8] space-y-6 text-white/85">
            <p>There is no standard solution because there is no standard smile.</p>
            <p>
              Thoughtful restorative care considers more than the tooth being treated. The bite, neighboring teeth, facial features, oral health, and personal priorities all influence the treatment plan.
            </p>
            <p>
              The most appropriate option is not always the most extensive one. It is the treatment that addresses the problem responsibly, preserves healthy tooth structure when possible, and supports lasting comfort and function.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container-x">
        <div className="max-w-4xl mx-auto border border-gold/25 bg-cream/50 p-8 md:p-12">
          <SectionHeading eyebrow="From Dr. K" title="A Personal" italicWord="Note" align="center" />
          <blockquote className="mt-8 text-center space-y-4">
            <p className="font-serif italic text-xl md:text-2xl leading-[1.7] text-ink/85">
              "I believe good dentistry begins with listening. I will take the time to understand what is bothering you, explain your options clearly, and help you choose a path that feels right. My goal is not simply to repair teeth - it is to help you feel healthy, comfortable, and confident in your smile again."
            </p>
            <footer className="text-sm tracking-[0.2em] uppercase text-gold">Dr. K</footer>
          </blockquote>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-cream/40">
        <div className="container-x max-w-4xl text-ink/80 leading-[1.85] space-y-6">
          <SectionHeading eyebrow="Next Step" title="Your Smile," italicWord="Beautifully Restored" />
          <p>
            You do not need to know which treatment you need before beginning the conversation. Start with what concerns you, what feels different, or what you would like to change.
          </p>
          <p>
            Restoring a smile is about more than replacing what has been lost. It is about bringing back comfort, function, and the freedom to feel like yourself again.
          </p>
        </div>
      </section>

      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.smileCenter} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
        </div>
        <div className="relative container-x text-center text-white">
          <h2 className="heading-display text-4xl md:text-6xl max-w-4xl mx-auto">
            Ready to Restore <span className="italic text-gold-light">Comfort and Confidence?</span>
          </h2>
          <p className="max-w-3xl mx-auto mt-6 text-white/80 leading-relaxed">
            Start with a personalized consultation and a clear plan built around your goals, your comfort, and your long-term oral health.
          </p>
          <Link to="/contact" className="btn-outline-light mt-10 inline-flex">Book a Consultation</Link>
        </div>
      </section>
    </div>
  );
};
